import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'
import type { ReactNode } from 'react'
import type { ConfirmationIconVariant } from './ConfirmationModalIcon'
import { ConfirmationModalIcon } from './ConfirmationModalIcon'
import {
  ConfirmationModalLayout,
  ConfirmationModalShell,
} from './ConfirmationModalShell'
import {
  confirmationBodySx,
  confirmationHeadlineSx,
  confirmationPrimaryButtonSx,
  confirmationSecondaryButtonSx,
} from './confirmationModalStyles'

type Props = Readonly<{
  open: boolean
  headline: string
  /** One or two centered body lines (Figma 4418:9974). */
  bodyLines?: readonly [string] | readonly [string, string]
  body?: ReactNode
  dismissLabel?: ReactNode
  confirmLabel?: ReactNode
  iconVariant?: ConfirmationIconVariant
  confirmDisabled?: boolean
  confirmPending?: boolean
  confirmButtonSx?: SxProps<Theme>
  onDismiss: () => void
  onConfirm: () => void
  /** When false, backdrop / escape do not dismiss (e.g. while publishing). */
  allowDismiss?: boolean
  dialogTestId?: string
  dismissTestId?: string
  confirmTestId?: string
}>

function renderBodyLines(bodyLines: readonly [string] | readonly [string, string] | undefined) {
  if (!bodyLines?.length) return null
  return (
    <Box sx={{ textAlign: 'center', maxWidth: 388, mx: 'auto' }}>
      {bodyLines.filter((line) => line.trim()).map((line) => (
        <Typography key={line} sx={confirmationBodySx}>
          {line}
        </Typography>
      ))}
    </Box>
  )
}

/** Figma 4418:9966 — OK / Yes / Confirm / Accept (green check icon). */
export function ConfirmPositiveModal({
  open,
  headline,
  bodyLines,
  body,
  dismissLabel = 'No',
  confirmLabel = 'Confirm',
  iconVariant = 'positive',
  confirmDisabled = false,
  confirmPending = false,
  confirmButtonSx,
  onDismiss,
  onConfirm,
  allowDismiss = true,
  dialogTestId = 'confirm-positive-dialog',
  dismissTestId = 'confirm-positive-dismiss',
  confirmTestId = 'confirm-positive-confirm',
}: Props) {
  const headlineId = `${dialogTestId}-headline`
  const bodyId = `${dialogTestId}-body`

  return (
    <ConfirmationModalShell
      open={open}
      onClose={allowDismiss ? onDismiss : undefined}
      disableEscapeKeyDown={!allowDismiss}
      dialogTestId={dialogTestId}
      ariaLabelledBy={headlineId}
      ariaDescribedBy={bodyId}
    >
      <ConfirmationModalLayout>
        <ConfirmationModalIcon variant={iconVariant} />

        <Box
          id={bodyId}
          sx={{
            width: '100%',
            px: '24px',
            pt: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Typography id={headlineId} component="h2" sx={confirmationHeadlineSx}>
            {headline}
          </Typography>
          {body ?? renderBodyLines(bodyLines)}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            width: '100%',
            px: 3,
            py: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={onDismiss}
            disabled={confirmPending}
            data-testid={dismissTestId}
            sx={confirmationSecondaryButtonSx}
          >
            {dismissLabel}
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={confirmDisabled || confirmPending}
            data-testid={confirmTestId}
            sx={confirmButtonSx ?? confirmationPrimaryButtonSx}
          >
            {confirmLabel}
          </Button>
        </Box>
      </ConfirmationModalLayout>
    </ConfirmationModalShell>
  )
}
